<template>
  <div class="w-full space-y-12 lg:space-y-20">
    <!-- Clean Modern Header -->
    <article class="w-full pb-12 lg:pb-20 pt-8">
      <div class="container">
        
        <!-- Header Content -->
        <div class="w-full lg:w-9/12 xl:w-8/12 mx-auto mb-6 lg:mb-10 text-center">
          <!-- Breadcrumb (Dark text for white background) -->
          <UiBreadcrumb :data="breadcrumbs" color="black" class="mb-6 justify-center" />
          
          <!-- Title -->
          <h1 class="text-2xl md:text-4xl lg:text-5xl font-bold font-saira text-gray-900 leading-tight mb-8">
            {{ newsDetailData?.[0]?.title }}
          </h1>
          
          <!-- Meta (Date & Read Time placeholder) -->
          <div class="flex items-center justify-center gap-6 text-gray-500 mb-2">

            <div class="flex items-center gap-3">
               <span class="text-sm font-medium">{{ $t("Share") }}:</span>
               <WidgetsShare :title="newsDetailData?.[0]?.title" />
            </div>
          </div>
        </div>

        <!-- Large Cover Image -->
        <div class="w-full lg:w-10/12 xl:w-9/12 mx-auto mb-8 lg:mb-16">
          <AtomsImage
            v-if="newsDetailData?.[0]?.listMedia || newsDetailData?.[0]?.media"
            :media="newsDetailData?.[0]?.listMedia || newsDetailData?.[0]?.media"
            class="w-full object-cover rounded-2xl lg:rounded-3xl aspect-[4/3] md:aspect-[16/9]  shadow-sm"
            sizeHint="content"
            :alt="newsDetailData?.[0]?.title"
          />
        </div>
        
        <!-- Content Container -->
        <div class="w-full lg:w-9/12 xl:w-8/12 mx-auto">
          <div class="w-full bg-white border border-gray-100 shadow-[0_4px_24px_-4px_rgba(0,0,0,0.04)] rounded-2xl md:rounded-3xl p-6 md:p-10 lg:p-12">
            <SectionsSectionRenderer 
              :sections="newsDetailData?.[0]?.sections" 
              :extraProps="{ isPlain: true }"
            />
          </div>
        </div>
      </div>
    </article>
  </div>
</template>
<script setup>
import { useApi } from "~/server/getApi";
import qs from "qs";
import { useI18n } from "vue-i18n";
import { useMetaTags } from "~/composables/headConfig";
import {
  buildJsonLdScripts,
  createFaqSchema,
  createWebPageSchema,
  createBreadcrumbSchema,
} from "~/composables/schemaOrg";
import { formatLocaleDate } from "~/composables/dateFormatter";
import { useSiteSettingsStore } from "@/store/index";

const { t, locale } = useI18n();
const siteSettingsStore = useSiteSettingsStore();
const route = useRoute();
const slug = computed(() => route.params.slug || "fallback-degeri");
const config = useRuntimeConfig();
const siteUrl = config.public.siteUrl;
const baseUrl = config.public.baseUrl;
const fullUrl = computed(() => `${siteUrl}${route.fullPath}`);

const formatDate = (value) => formatLocaleDate(value);

const mainQuery = computed(() =>
  qs.stringify(
    {
      filters: { slug: { $eq: slug.value } },
    },
    { encodeValuesOnly: true },
  ),
);

const { data: newsDetailData, fetchData: fetchNewsDetailData } = useApi(
  `news-lists?${mainQuery.value}&pLevel`,
);

await fetchNewsDetailData();

/* -------------------- 🍞 Breadcrumb -------------------- */
const breadcrumbs = computed(() => [
  {
    enabled: true,
    link: locale.value === "tr" ? "/" : `/${locale.value}`,
    title: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 md:w-5 md:h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M2.25 12l8.954-8.955a1.125 1.125 0 011.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" /></svg>`,
  },
  {
    enabled: true,
    link: `/${t("Routes.News").toLowerCase()}`,
    title: t("Breadcrumb.News"),
  },
  {
    enabled: true,
    link: `/${slug.value}`,
    title: newsDetailData.value?.[0]?.title,
  },
]);

/* -------------------- 🔧 SEO Etiketi -------------------- */
const seoData = computed(() => newsDetailData.value?.[0]?.seo ?? {});
const pageTitle = computed(() => newsDetailData.value?.[0]?.title);
const pageDescription = computed(
  () => newsDetailData.value?.[0]?.description,
);

useMetaTags(seoData, pageTitle, pageDescription);

/* -------------------- 📄 UseHead Schema for Page -------------------- */
useHead(() => {
  const page = newsDetailData.value?.[0];
  if (!page) {
    return { script: [] };
  }

  const faqSection = page.sections?.find(
    (section) =>
      ["sections.accordion", "sections.image-accordion"].includes(
        section.__component,
      ) && section.enabled,
  );

  const scripts = buildJsonLdScripts([
    {
      key: "schema-news-detail",
      schema: createWebPageSchema({
        type: "NewsArticle",
        url: fullUrl.value,
        name: page.title,
        headline: page.title,
        description: page.seo?.description || page.description,
        publishedAt: page.publishedAt,
        updatedAt: page.updatedAt,
        keywords: page.seo?.keywords,
        baseUrl,
        mainEntityType: "NewsArticle",
      }),
    },
    {
      key: "schema-news-detail-faq",
      schema: createFaqSchema(faqSection?.items),
    },
    {
      key: "schema-news-detail-breadcrumbs",
      schema: createBreadcrumbSchema(breadcrumbs.value, siteUrl),
    },
  ]);

  return {
    script: scripts,
  };
});
</script>
